import { ApolloQueryResult, useQuery } from "@apollo/client";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { GET_CB_TIPPER_PROFILE } from "../queries/mutation/cb/cb_queries";
import { GET_DISCORD_PROFILE } from "../queries/mutation/discord/queries";
import { useAuth } from "./AuthProvider";

type CBProfile = {
  username: string;
  inFanclub: boolean;
  createdDatetime: string;
  lifetimeBiggestTip: number;
  numberOfTips: number;
  broadcaster: string;
};
type DiscordProfile = { username: string; discordId?: string };
interface ProfilesContextType {
  discordProfile: DiscordProfile | null;
  refetchDiscordProfile: () => Promise<ApolloQueryResult<any>>;
  refetchCBProfile: () => Promise<ApolloQueryResult<any>>;
  cbProfile: CBProfile | null;
  discordLoading: boolean;
  cbLoading: boolean;
}

const ProfilesContext = createContext<ProfilesContextType | undefined>(
  undefined
);

interface ProfilesProviderProps {
  children: ReactNode;
}

// Context used to get profiles (discord, cb) and handles to refetch
export const ProfilesProvider: React.FC<ProfilesProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const [cbProfile, setCBProfile] = useState<CBProfile | null>(null);
  const [discordProfile, setDiscordProfile] = useState<DiscordProfile | null>(
    null
  );

  const skip = user?.isBroadcaster ?? true;
  const {
    data: discordProfileData,
    loading: discordLoading,
    refetch: refetchDiscordProfile,
  } = useQuery(GET_DISCORD_PROFILE, { skip });

  const {
    data: cbProfileData,
    refetch: refetchCBProfile,
    loading: cbLoading,
  } = useQuery(GET_CB_TIPPER_PROFILE, { skip });

  useEffect(() => {
    refetchDiscordProfile();
    refetchCBProfile();
  }, [user?.username]);

  useEffect(() => {
    if (discordProfileData) {
      setDiscordProfile(discordProfileData.getDiscordProfile);
    }
  }, [discordProfileData]);

  useEffect(() => {
    if (cbProfileData) {
      setCBProfile(cbProfileData.getCBTipperProfile);
    }
  }, [cbProfileData]);

  return (
    <ProfilesContext.Provider
      value={{
        cbProfile,
        refetchCBProfile,
        discordProfile,
        refetchDiscordProfile,
        cbLoading,
        discordLoading,
      }}
    >
      {children}
    </ProfilesContext.Provider>
  );
};

// Custom hook to use authentication context
export const useProfiles = (): ProfilesContextType => {
  const context = useContext(ProfilesContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
